import { apiDelete, apiGet, apiPatch, apiPost, apiPut } from '@/shared/utils/requestBuilder';
import type {
  Arbitro,
  ArbitroStatus,
  ConviteStatus,
  CreateArbitroDto,
  Especialidade,
  HistoricoArbitro,
  PeriodoIndisponibilidade,
  PesoArbitroInput,
  UpdateArbitroDto,
} from '@/types/arbitro/arbitroTypes';

const BASE = '/admin/arbitral';

type SpecialtyDTO = { id: string; name: string; active: boolean; arbiterCount: number };
type ArbiterDTO = {
  id: string;
  name: string;
  document: string | null;
  oabNumber: string | null;
  oabState: string | null;
  email: string;
  phone: string | null;
  specialties: SpecialtyDTO[];
  status: 'active' | 'inactive';
  weight: number;
  inviteStatus: 'not_sent' | 'pending' | 'accepted';
  unavailabilities: PeriodoIndisponibilidade[];
  createdAt: string;
};

const STATUS_TO_PT: Record<ArbiterDTO['status'], ArbitroStatus> = {
  active: 'ativo',
  inactive: 'inativo',
};
const INVITE_TO_PT: Record<ArbiterDTO['inviteStatus'], ConviteStatus> = {
  not_sent: 'nao_enviado',
  pending: 'pendente',
  accepted: 'aceito',
};

const toEspecialidade = (dto: SpecialtyDTO): Especialidade => ({
  id: dto.id,
  nome: dto.name,
  ativo: dto.active,
  totalArbitros: dto.arbiterCount,
});

const toArbitro = (dto: ArbiterDTO): Arbitro => ({
  id: dto.id,
  nome: dto.name,
  documento: dto.document ?? '',
  oabNumero: dto.oabNumber ?? '',
  oabUf: dto.oabState ?? '',
  email: dto.email,
  telefone: dto.phone,
  especialidades: dto.specialties.map(toEspecialidade),
  status: STATUS_TO_PT[dto.status],
  peso: dto.weight,
  conviteStatus: INVITE_TO_PT[dto.inviteStatus],
  indisponibilidades: dto.unavailabilities ?? [],
  criadoEm: dto.createdAt,
});

async function unwrap<T>(response: Response): Promise<T> {
  const json = (await response.json().catch(() => null)) as {
    data?: T;
    error?: { message?: string };
  } | null;
  if (!response.ok) {
    throw new Error(json?.error?.message || 'Não foi possível concluir a operação.');
  }
  return json?.data as T;
}

export const arbitrosService = {
  listar: async (): Promise<Arbitro[]> => {
    const data = await unwrap<{ items: ArbiterDTO[] }>(await apiGet(`${BASE}/arbitros`));
    return data.items.map(toArbitro);
  },

  listarEspecialidades: async (): Promise<Especialidade[]> => {
    const data = await unwrap<{ items: SpecialtyDTO[] }>(await apiGet(`${BASE}/especialidades`));
    return data.items.map(toEspecialidade);
  },

  criarEspecialidade: async (nome: string): Promise<Especialidade> => {
    const dto = await unwrap<SpecialtyDTO>(await apiPost(`${BASE}/especialidades`, { name: nome }));
    return toEspecialidade(dto);
  },

  criar: async (data: CreateArbitroDto): Promise<Arbitro> => {
    const dto = await unwrap<ArbiterDTO>(
      await apiPost(`${BASE}/arbitros`, {
        name: data.nome,
        document: data.documento,
        oabNumber: data.oabNumero,
        oabState: data.oabUf,
        email: data.email,
        phone: data.telefone ?? null,
        specialtyIds: data.especialidadeIds,
      }),
    );
    return toArbitro(dto);
  },

  renomearEspecialidade: async (id: string, nome: string): Promise<Especialidade> => {
    const dto = await unwrap<SpecialtyDTO>(
      await apiPatch(`${BASE}/especialidades/${id}`, { name: nome }),
    );
    return toEspecialidade(dto);
  },

  alternarStatusEspecialidade: async (id: string): Promise<Especialidade> => {
    const dto = await unwrap<SpecialtyDTO>(await apiPatch(`${BASE}/especialidades/${id}/status`));
    return toEspecialidade(dto);
  },

  atualizarPesos: async (pesos: PesoArbitroInput[]): Promise<Arbitro[]> => {
    await unwrap<{ items: unknown[] }>(
      await apiPut(`${BASE}/ranking-arbitros`, {
        itens: pesos.map((item) => ({ arbitroId: item.arbitroId, peso: item.peso })),
      }),
    );
    const data = await unwrap<{ items: ArbiterDTO[] }>(await apiGet(`${BASE}/arbitros`));
    return data.items.map(toArbitro);
  },

  alterarStatus: async (id: string, status: ArbitroStatus): Promise<Arbitro> => {
    const dto = await unwrap<ArbiterDTO>(
      await apiPatch(`${BASE}/arbitros/${id}/status`, { active: status === 'ativo' }),
    );
    return toArbitro(dto);
  },

  buscarHistorico: async (id: string): Promise<HistoricoArbitro[]> => {
    const data = await unwrap<{ items: HistoricoArbitro[] }>(
      await apiGet(`${BASE}/arbitros/${id}/historico`),
    );
    return data.items;
  },

  reenviarConvite: async (id: string): Promise<Arbitro> => {
    const dto = await unwrap<ArbiterDTO>(await apiPost(`${BASE}/arbitros/${id}/reenviar-convite`));
    return toArbitro(dto);
  },

  atualizar: async (id: string, data: UpdateArbitroDto): Promise<Arbitro> => {
    const dto = await unwrap<ArbiterDTO>(
      await apiPatch(`${BASE}/arbitros/${id}`, {
        name: data.nome,
        email: data.email,
        phone: data.telefone ?? null,
        oabNumber: data.oabNumero,
        oabState: data.oabUf,
        specialtyIds: data.especialidadeIds,
      }),
    );
    return toArbitro(dto);
  },

  registrarIndisponibilidade: async (
    id: string,
    periodo: Omit<PeriodoIndisponibilidade, 'id'>,
  ): Promise<Arbitro> => {
    const dto = await unwrap<ArbiterDTO>(
      await apiPost(`${BASE}/arbitros/${id}/indisponibilidades`, {
        inicio: periodo.inicio,
        fim: periodo.fim,
        motivo: periodo.motivo,
      }),
    );
    return toArbitro(dto);
  },

  removerIndisponibilidade: async (id: string, periodoId: string): Promise<Arbitro> => {
    const dto = await unwrap<ArbiterDTO>(
      await apiDelete(`${BASE}/arbitros/${id}/indisponibilidades/${periodoId}`),
    );
    return toArbitro(dto);
  },
};
